import { translate } from '@/ui/i18n';

import styles from '../styles/VenueDateManagement.module.css';

type Props = {
  data: {
    parity: {
      men: number;
      women: number;
      other: number;
    };
  };
};

enum Colors {
  First = '#3A56A5',
  Second = '#6882CA',
  Third = '#A4B4DF',
}

// Updated ParityData type to reflect the structure returned by assignColorsByPercentage
type ParityDataResult = {
  women: {
    value: number;
    color: Colors;
  };
  other: {
    value: number;
    color: Colors;
  };
  men: {
    value: number;
    color: Colors;
  };
};

export function ParityBar(props: Props) {
  const { data } = props;
  if (!data.parity) return;
  const total = data.parity.men + data.parity.women + data.parity.other;

  // Calculate raw percentages (needed for labels)
  const percentages = Object.keys(data.parity)
    .filter((item) => item !== undefined)
    .reduce(
      (acc, key) => {
        const value = data.parity[key as keyof typeof data.parity];
        return {
          ...acc,
          [key]: Math.round((value / total) * 100),
        };
      },
      {} as Record<string, number>,
    );

  // Assign colors based on percentages
  const parityData = assignColorsByPercentage(percentages);
  parityData.other.value = parityData.other.value || 1;

  return (
    <div>
      <div className={styles.parityBar}>
        {/* Dynamically set width and background color */}
        <div
          className={styles.parityFirst}
          style={{
            width: `${parityData.women.value || 1}%`,
            backgroundColor: parityData.women.color,
          }}
        />
        <div
          className={styles.paritySecond}
          style={{
            width: `${parityData.other.value || 1}%`,
            backgroundColor: parityData.other.color,
          }}
        />
        <div
          className={styles.parityThird}
          style={{
            width: `${parityData.men.value || 1}%`,
            backgroundColor: parityData.men.color,
          }}
        />
      </div>
      <div className={styles.parityLabels}>
        <span>
          {translate('PdfExports.women')} ({data.parity.women})
        </span>
        <span>
          {translate('PdfExports.other')} ({data.parity.other})
        </span>
        <span>
          {translate('PdfExports.men')} ({data.parity.men})
        </span>
      </div>
    </div>
  );
}

/**
 * Assigns colors based on the ranking of percentages, handling ties with randomness.
 * @param percentages A record of percentage values for each parity group.
 * @returns An object with each parity group assigned a color based on its percentage ranking.
 */
const assignColorsByPercentage = (
  percentages: Record<string, number>,
): ParityDataResult => {
  // Use the more specific type here
  // The ParityData type within assignColorsByPercentage should refer to the structure it takes in, not the output type
  type PercentageEntry = { key: keyof typeof percentages; value: number };

  const sortedPercentages: PercentageEntry[] = Object.entries(percentages)
    .map(([key, value]) => ({
      key: key as keyof typeof percentages,
      value: value,
    }))
    .sort((a, b) => b.value - a.value); // Sort in descending order

  const coloredResults: {
    [key in keyof ParityDataResult]?: { value: number; color: Colors };
  } = {};
  const availableColors = [Colors.First, Colors.Second, Colors.Third];

  let colorIndex = 0;
  for (let i = 0; i < sortedPercentages.length; i++) {
    const current = sortedPercentages[i];
    const next = sortedPercentages[i + 1];

    if (next && current.value === next.value) {
      const tiedElements: PercentageEntry[] = [current];
      let j = i + 1;
      while (
        j < sortedPercentages.length &&
        sortedPercentages[j].value === current.value
      ) {
        tiedElements.push(sortedPercentages[j]);
        j++;
      }

      const shuffledTiedElements = [...tiedElements].sort(
        () => Math.random() - 0.5,
      );
      shuffledTiedElements.forEach((element) => {
        const colorToAssign =
          availableColors[colorIndex % availableColors.length];
        coloredResults[element.key as keyof ParityDataResult] = {
          // Type assertion for assignment
          value: element.value,
          color: colorToAssign,
        };
        colorIndex++;
      });
      i = j - 1;
    } else {
      coloredResults[current.key as keyof ParityDataResult] = {
        // Type assertion for assignment
        value: current.value,
        color: availableColors[colorIndex % availableColors.length],
      };
      colorIndex++;
    }
  }

  // Ensure all keys are present, providing defaults if not explicitly assigned
  return {
    men: coloredResults.men || { value: percentages.men, color: Colors.First },
    women: coloredResults.women || {
      value: percentages.women,
      color: Colors.Second,
    },
    other: coloredResults.other || {
      value: percentages.other,
      color: Colors.Third,
    },
  } as ParityDataResult; // Cast to the final expected type
};
